import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  CreatePlanPayload,
  PlatformSettings,
  SubscriptionPlan,
  SystemStatus,
} from '../../services/admin.service';
import { adminService } from '../../services/admin.service';
import { useAuthStore } from '../../store/auth.store';

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

interface PlanFormState {
  name: string;
  title: string;
  description: string;
  price_monthly: string;
  duration_days: string;
  daily_notification_limit: string;
  max_shops: string;
  max_qr_codes: string;
  features: string[];
  is_active: boolean;
  is_popular: boolean;
  sort_order: string;
}

type PlanFormErrors = Partial<Record<keyof PlanFormState, string>>;
type DrawerMode = 'add' | 'edit';
type TabId = 'profile' | 'platform' | 'plans' | 'system';

const EMPTY_PLAN_FORM: PlanFormState = {
  name: '',
  title: '',
  description: '',
  price_monthly: '',
  duration_days: '30',
  daily_notification_limit: '-1',
  max_shops: '1',
  max_qr_codes: '1',
  features: [''],
  is_active: true,
  is_popular: false,
  sort_order: '0',
};

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  function showToast(type: Toast['type'], message: string) {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3500);
  }

  return { toasts, showToast };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {toast.message}
        </div>
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
      {children}
    </p>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  error,
  placeholder,
  suffix,
  prefix,
  disabled = false,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  error?: string;
  placeholder?: string;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  disabled?: boolean;
  helperText?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700">{label}</label>
      <div className="relative">
        {prefix ? (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-500">
            {prefix}
          </div>
        ) : null}
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full rounded-lg border bg-slate-50 py-2 text-sm text-slate-900 outline-none transition-colors focus:ring-1 ${
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
              : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-200'
          } ${prefix ? 'pl-9' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'} ${
            disabled ? 'cursor-not-allowed bg-slate-100 text-slate-500' : ''
          }`}
        />
        {suffix ? (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-xs text-slate-500">
            {suffix}
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-[11px] text-red-500">{error}</p> : null}
      {!error && helperText ? <p className="mt-1 text-[11px] text-slate-400">{helperText}</p> : null}
    </div>
  );
}

function TextareaField({
  label,
  value,
  onChange,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700">{label}</label>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={4}
        className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:ring-1 ${
          error
            ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
            : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-200'
        }`}
      />
      {error ? <p className="mt-1 text-[11px] text-red-500">{error}</p> : null}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? 'bg-indigo-500' : 'bg-slate-300'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function usePasswordVisibility() {
  const [visible, setVisible] = useState(false);

  return {
    inputType: visible ? 'text' : 'password',
    icon: (
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((current) => !current)}
        className="text-slate-400 transition-colors hover:text-slate-600"
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        <EyeIcon open={visible} />
      </button>
    ),
  };
}

function LineSkeleton({ width = 'w-3/4' }: { width?: string }) {
  return <div className={`h-3.5 animate-pulse rounded bg-slate-200 ${width}`} />;
}

function StatusDot({ status }: { status: 'connected' | 'unreachable' | undefined }) {
  if (!status) return <div className="h-2 w-2 animate-pulse rounded-full bg-slate-300" />;

  return (
    <div className={`h-2 w-2 rounded-full ${status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
  );
}

function normalizePlanForm(plan?: SubscriptionPlan): PlanFormState {
  if (!plan) return EMPTY_PLAN_FORM;

  return {
    name: plan.name,
    title: plan.title,
    description: plan.description,
    price_monthly: String(plan.price_monthly),
    duration_days: String(plan.duration_days),
    daily_notification_limit: String(plan.daily_notification_limit),
    max_shops: String(plan.max_shops),
    max_qr_codes: String(plan.max_qr_codes),
    features: plan.features.length > 0 ? [...plan.features] : [''],
    is_active: plan.is_active,
    is_popular: plan.is_popular,
    sort_order: String(plan.sort_order),
  };
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function AdminSettingsPage() {
  const { toasts, showToast } = useToast();
  const user = useAuthStore((state) => state.user);

  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileNameError, setProfileNameError] = useState('');
  const [profileEmailError, setProfileEmailError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [currentPwError, setCurrentPwError] = useState('');
  const [newPwError, setNewPwError] = useState('');
  const [confirmPwError, setConfirmPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const currentPwVis = usePasswordVisibility();
  const newPwVis = usePasswordVisibility();
  const confirmPwVis = usePasswordVisibility();

  const [platform, setPlatform] = useState<PlatformSettings | null>(null);
  const [platformLoading, setPlatformLoading] = useState(true);

  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('add');
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState<PlanFormState>(EMPTY_PLAN_FORM);
  const [planErrors, setPlanErrors] = useState<PlanFormErrors>({});
  const [planSaving, setPlanSaving] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  const fetchProfile = useCallback(async () => {
    try {
      const data = await adminService.getAdminProfile();
      setProfileName(data.name);
      setProfileEmail(data.email);
    } catch {
      setProfileEmail(user?.email ?? '');
    } finally {
      setProfileLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    void adminService
      .getPlatformSettings()
      .then((data) => {
        setPlatform(data);
      })
      .finally(() => {
        setPlatformLoading(false);
      });
  }, []);

  const fetchStatus = useCallback(async (isManual = false) => {
    if (isManual) {
      setRefreshing(true);
    } else {
      setStatusLoading(true);
    }

    setStatusError('');

    try {
      const data = await adminService.getSystemStatus();
      setStatus(data);
      setLastUpdated(new Date());
    } catch (error) {
      setStatusError(getErrorMessage(error, 'Failed to load system status'));
    } finally {
      setStatusLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
    const intervalId = setInterval(() => {
      void fetchStatus();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchStatus]);

  const fetchPlans = useCallback(async () => {
    setPlansError('');
    setPlansLoading(true);

    try {
      const data = await adminService.getSubscriptionPlans();
      setPlans(data);
    } catch (error) {
      setPlansError(getErrorMessage(error, 'Failed to load subscription plans'));
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  const planRows = platform
    ? [
        { plan: 'Trial', limit: platform.planLimits.trial },
        { plan: 'Basic', limit: platform.planLimits.basic },
        { plan: 'Pro', limit: platform.planLimits.pro },
        { plan: 'Enterprise', limit: platform.planLimits.enterprise },
      ]
    : [];

  const nextSortOrder = useMemo(() => {
    if (plans.length === 0) return 0;
    return Math.max(...plans.map((plan) => plan.sort_order)) + 1;
  }, [plans]);

  function openAddDrawer() {
    setDrawerMode('add');
    setEditingPlanId(null);
    setPlanErrors({});
    setPlanForm({ ...EMPTY_PLAN_FORM, sort_order: String(nextSortOrder) });
    setDrawerOpen(true);
  }

  function openEditDrawer(plan: SubscriptionPlan) {
    setDrawerMode('edit');
    setEditingPlanId(plan.id);
    setPlanErrors({});
    setPlanForm(normalizePlanForm(plan));
    setDrawerOpen(true);
  }

  function closeDrawer() {
    if (planSaving) return;
    setDrawerOpen(false);
    setEditingPlanId(null);
    setPlanErrors({});
  }

  function updatePlanField<Key extends keyof PlanFormState>(key: Key, value: PlanFormState[Key]) {
    setPlanForm((current) => ({ ...current, [key]: value }));
    setPlanErrors((current) => ({ ...current, [key]: undefined }));
  }

  function updateFeature(index: number, value: string) {
    setPlanForm((current) => ({
      ...current,
      features: current.features.map((feature, featureIndex) =>
        featureIndex === index ? value : feature
      ),
    }));
    setPlanErrors((current) => ({ ...current, features: undefined }));
  }

  function addFeature() {
    setPlanForm((current) => ({ ...current, features: [...current.features, ''] }));
    setPlanErrors((current) => ({ ...current, features: undefined }));
  }

  function removeFeature(index: number) {
    setPlanForm((current) => {
      const nextFeatures = current.features.filter((_, featureIndex) => featureIndex !== index);
      return {
        ...current,
        features: nextFeatures.length > 0 ? nextFeatures : [''],
      };
    });
    setPlanErrors((current) => ({ ...current, features: undefined }));
  }

  function validatePlanForm() {
    const errors: PlanFormErrors = {};

    const normalizedName = planForm.name.trim().toLowerCase();
    const normalizedFeatures = planForm.features.map((feature) => feature.trim()).filter(Boolean);

    if (!normalizedName) {
      errors.name = 'Plan name is required';
    } else if (!/^[a-z0-9_-]+$/.test(normalizedName)) {
      errors.name = 'Use lowercase letters, numbers, hyphens, or underscores only';
    }

    if (!planForm.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!planForm.description.trim()) {
      errors.description = 'Description is required';
    }

    const price = Number(planForm.price_monthly);
    if (planForm.price_monthly.trim() === '' || Number.isNaN(price) || price < 0) {
      errors.price_monthly = 'Enter a valid monthly price';
    }

    const duration = Number(planForm.duration_days);
    if (!Number.isInteger(duration) || duration < 1) {
      errors.duration_days = 'Duration must be at least 1 day';
    }

    const notificationLimit = Number(planForm.daily_notification_limit);
    if (!Number.isInteger(notificationLimit) || notificationLimit < -1) {
      errors.daily_notification_limit = 'Use -1 or a whole number greater than -1';
    }

    const maxShops = Number(planForm.max_shops);
    if (!Number.isInteger(maxShops) || maxShops < 1) {
      errors.max_shops = 'Max shops must be at least 1';
    }

    const maxQrCodes = Number(planForm.max_qr_codes);
    if (!Number.isInteger(maxQrCodes) || maxQrCodes < 1) {
      errors.max_qr_codes = 'Max QR codes must be at least 1';
    }

    const sortOrder = Number(planForm.sort_order);
    if (!Number.isInteger(sortOrder)) {
      errors.sort_order = 'Sort order must be a whole number';
    }

    if (normalizedFeatures.length === 0) {
      errors.features = 'Add at least one feature';
    } else if (normalizedFeatures.length !== planForm.features.length) {
      errors.features = 'Feature rows cannot be empty';
    }

    setPlanErrors(errors);

    if (Object.keys(errors).length > 0) {
      return null;
    }

    const payload: CreatePlanPayload = {
      name: normalizedName,
      title: planForm.title.trim(),
      description: planForm.description.trim(),
      price_monthly: price,
      duration_days: duration,
      daily_notification_limit: notificationLimit,
      max_shops: maxShops,
      max_qr_codes: maxQrCodes,
      features: normalizedFeatures,
      is_active: planForm.is_active,
      is_popular: planForm.is_popular,
      sort_order: sortOrder,
    };

    return payload;
  }

  async function handleSaveProfile(event: React.FormEvent) {
    event.preventDefault();

    let hasError = false;
    setProfileNameError('');
    setProfileEmailError('');

    if (!profileName.trim()) {
      setProfileNameError('Name is required');
      hasError = true;
    }

    if (!profileEmail.trim()) {
      setProfileEmailError('Email is required');
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileEmail)) {
      setProfileEmailError('Enter a valid email address');
      hasError = true;
    }

    if (hasError) return;

    setProfileSaving(true);

    try {
      await adminService.updateAdminProfile({
        name: profileName.trim(),
        email: profileEmail.trim(),
      });
      showToast('success', 'Profile saved');
      await fetchProfile();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to save profile');
      if (message.toLowerCase().includes('email')) {
        setProfileEmailError(message);
      } else {
        showToast('error', message);
      }
    } finally {
      setProfileSaving(false);
    }
  }

  async function handleUpdatePassword(event: React.FormEvent) {
    event.preventDefault();

    let hasError = false;
    setCurrentPwError('');
    setNewPwError('');
    setConfirmPwError('');

    if (!currentPw) {
      setCurrentPwError('Current password is required');
      hasError = true;
    }

    if (newPw.length < 10) {
      setNewPwError('Must be at least 10 characters');
      hasError = true;
    } else if (!/\d/.test(newPw)) {
      setNewPwError('Must contain at least one number');
      hasError = true;
    }

    if (!confirmPw) {
      setConfirmPwError('Please confirm your new password');
      hasError = true;
    } else if (newPw !== confirmPw) {
      setConfirmPwError('Passwords do not match');
      hasError = true;
    }

    if (hasError) return;

    setPwSaving(true);

    try {
      await adminService.updateAdminPassword({ currentPassword: currentPw, newPassword: newPw });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      showToast('success', 'Password updated');
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update password');
      if (message.toLowerCase().includes('current')) {
        setCurrentPwError(message);
      } else {
        showToast('error', message);
      }
    } finally {
      setPwSaving(false);
    }
  }

  async function handleSubmitPlan(event: React.FormEvent) {
    event.preventDefault();

    const payload = validatePlanForm();
    if (!payload) return;

    setPlanSaving(true);

    try {
      if (drawerMode === 'add') {
        const created = await adminService.createSubscriptionPlan(payload);
        setPlans((current) =>
          [...current, created].sort((left, right) => left.sort_order - right.sort_order)
        );
        showToast('success', 'Plan created');
      } else if (editingPlanId) {
        const updated = await adminService.updateSubscriptionPlan(editingPlanId, payload);
        setPlans((current) =>
          current
            .map((plan) => (plan.id === editingPlanId ? updated : plan))
            .sort((left, right) => left.sort_order - right.sort_order)
        );
        showToast('success', 'Plan updated');
      }

      setDrawerOpen(false);
      setEditingPlanId(null);
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to save plan');
      if (message.toLowerCase().includes('name')) {
        setPlanErrors((current) => ({ ...current, name: message }));
      } else {
        showToast('error', message);
      }
    } finally {
      setPlanSaving(false);
    }
  }

  async function handleTogglePlan(planId: string) {
    setToggleLoadingId(planId);

    try {
      const updated = await adminService.togglePlanActive(planId);
      setPlans((current) => current.map((plan) => (plan.id === planId ? updated : plan)));
      showToast('success', updated.is_active ? 'Plan activated' : 'Plan deactivated');
    } catch (error) {
      showToast('error', getErrorMessage(error, 'Failed to update plan status'));
    } finally {
      setToggleLoadingId(null);
    }
  }

  async function handleDeletePlan(planId: string) {
    setDeleteLoadingId(planId);

    try {
      await adminService.deleteSubscriptionPlan(planId);
      setPlans((current) => current.filter((plan) => plan.id !== planId));
      setDeleteConfirmId(null);
      showToast('success', 'Plan deleted');
    } catch (error) {
      showToast('error', getErrorMessage(error, 'Failed to delete plan'));
    } finally {
      setDeleteLoadingId(null);
    }
  }

  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <h1 className="mb-4 text-[20px] font-semibold text-slate-950">Settings</h1>

        <div style={{ borderBottom: '1px solid #E2E8F0', marginBottom: '24px', display: 'flex' }}>
          {(
            [
              {
                id: 'profile' as const,
                label: 'Profile',
                icon: (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                ),
              },
              {
                id: 'platform' as const,
                label: 'Platform',
                icon: (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                  </svg>
                ),
              },
              {
                id: 'plans' as const,
                label: 'Plans',
                icon: (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                ),
              },
              {
                id: 'system' as const,
                label: 'System',
                icon: (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                ),
              },
            ] as Array<{ id: TabId; label: string; icon: JSX.Element }>
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? 500 : 400,
                color: activeTab === tab.id ? '#6366F1' : '#64748B',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #6366F1' : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'profile' && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-indigo-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <p className="text-[14px] font-semibold text-slate-950">Profile</p>
            </div>
          </div>

          <div className="px-6 py-5">
            {profileLoading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="h-3 w-8 animate-pulse rounded bg-slate-200" />
                  <div className="h-9 animate-pulse rounded-lg bg-slate-100" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-8 animate-pulse rounded bg-slate-200" />
                  <div className="h-9 animate-pulse rounded-lg bg-slate-100" />
                </div>
              </div>
            ) : (
              <form onSubmit={(event) => void handleSaveProfile(event)} noValidate>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Name"
                    value={profileName}
                    onChange={setProfileName}
                    error={profileNameError}
                    placeholder="Your name"
                  />
                  <Field
                    label="Email"
                    value={profileEmail}
                    onChange={setProfileEmail}
                    type="email"
                    error={profileEmailError}
                    placeholder="admin@example.com"
                  />
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={profileSaving}
                    className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-60"
                  >
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}

            <div className="my-5 border-t border-slate-100" />

            <SectionLabel>Change Password</SectionLabel>
            <form onSubmit={(event) => void handleUpdatePassword(event)} noValidate className="mt-4 space-y-4">
              <Field
                label="Current password"
                value={currentPw}
                onChange={setCurrentPw}
                type={currentPwVis.inputType}
                error={currentPwError}
                suffix={currentPwVis.icon}
              />
              <Field
                label="New password"
                value={newPw}
                onChange={setNewPw}
                type={newPwVis.inputType}
                error={newPwError}
                placeholder="Min 10 chars, must include a number"
                suffix={newPwVis.icon}
              />
              <Field
                label="Confirm new password"
                value={confirmPw}
                onChange={setConfirmPw}
                type={confirmPwVis.inputType}
                error={confirmPwError}
                suffix={confirmPwVis.icon}
              />
              <button
                type="submit"
                disabled={pwSaving}
                className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-60"
              >
                {pwSaving ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
        )}

        {activeTab === 'platform' && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-indigo-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              <p className="text-[14px] font-semibold text-slate-950">Platform defaults</p>
            </div>
          </div>

          <div className="px-6 py-5">
            {platformLoading ? (
              <div className="space-y-3">
                <LineSkeleton width="w-1/3" />
                <LineSkeleton width="w-1/2" />
                <LineSkeleton width="w-2/3" />
              </div>
            ) : platform ? (
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg bg-slate-50 px-4 py-3">
                    <p className="text-[11px] text-slate-400">Default trial duration</p>
                    <p className="mt-0.5 text-[15px] font-semibold text-slate-950">
                      {platform.defaultTrialDays} days
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-4 py-3">
                    <p className="text-[11px] text-slate-400">Default prep time</p>
                    <p className="mt-0.5 text-[15px] font-semibold text-slate-950">
                      {platform.defaultPrepTimeMinutes} minutes
                    </p>
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-[11px] font-medium text-slate-400">
                    Default notification message
                  </p>
                  <blockquote className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50 px-4 py-3 text-[13px] italic text-slate-700">
                    {platform.defaultNotificationMessage}
                  </blockquote>
                </div>

                <div>
                  <SectionLabel>Plan limits (daily notifications)</SectionLabel>
                  <div className="mt-2 overflow-hidden rounded-lg border border-slate-100">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                            Plan
                          </th>
                          <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                            Daily limit
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {planRows.map(({ plan, limit }) => (
                          <tr key={plan}>
                            <td className="px-4 py-2.5 font-medium capitalize text-slate-950">
                              {plan}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">
                              {limit === -1 ? 'Unlimited' : `${limit} notifications`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    Contact support to customise plan limits
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        )}

        {activeTab === 'system' && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-indigo-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <p className="text-[14px] font-semibold text-slate-950">System status</p>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated ? (
                <span className="text-[11px] text-slate-400">
                  Updated{' '}
                  {lastUpdated.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </span>
              ) : null}
              <button
                type="button"
                disabled={refreshing || statusLoading}
                onClick={() => void fetchStatus(true)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="px-6 py-5">
            {statusError ? <p className="mb-4 text-[13px] text-red-600">{statusError}</p> : null}

            <SectionLabel>Services</SectionLabel>
            <div className="mt-3 space-y-2.5">
              {[
                { label: 'Database', key: 'database' as const },
                { label: 'Redis', key: 'redis' as const },
              ].map(({ label, key }) => (
                <div key={key} className="flex items-center gap-3">
                  {statusLoading ? (
                    <div className="h-2 w-2 animate-pulse rounded-full bg-slate-200" />
                  ) : (
                    <StatusDot status={status?.[key]} />
                  )}
                  <span className="text-[13px] text-slate-700">{label}</span>
                  {!statusLoading && status ? (
                    <span
                      className={`text-[12px] font-medium ${
                        status[key] === 'connected' ? 'text-green-600' : 'text-red-500'
                      }`}
                    >
                      {status[key] === 'connected' ? 'Connected' : 'Unreachable'}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="my-5 border-t border-slate-100" />

            <SectionLabel>Today's metrics</SectionLabel>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[
                { label: 'Total shops', value: status?.totalShops },
                { label: 'Active shops', value: status?.activeShops },
                { label: 'Active subscriptions', value: status?.activeSubscriptions },
                { label: 'Notifications today', value: status?.totalNotificationsSentToday },
                { label: 'Orders today', value: status?.totalOrdersToday },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg bg-slate-50 px-4 py-3">
                  {statusLoading || value === undefined ? (
                    <div className="mb-1 h-6 w-12 animate-pulse rounded bg-slate-200" />
                  ) : (
                    <p className="text-[20px] font-semibold leading-none text-slate-950">{value}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {activeTab === 'plans' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-indigo-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <div>
                <p className="text-[14px] font-semibold text-slate-950">Subscription Plans</p>
                <p className="text-[12px] text-slate-400">
                  Manage pricing, plan limits, and highlighted plans.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={openAddDrawer}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600"
            >
              Add New Plan
            </button>
          </div>

          <div className="px-6 py-5">
            {plansError ? <p className="mb-4 text-[13px] text-red-600">{plansError}</p> : null}

            {plansLoading ? (
              <div className="grid gap-4 md:grid-cols-2">
                {[0, 1].map((item) => (
                  <div key={item} className="rounded-xl border border-slate-200 p-5">
                    <div className="space-y-3">
                      <LineSkeleton width="w-1/2" />
                      <LineSkeleton width="w-5/6" />
                      <LineSkeleton width="w-1/3" />
                      <LineSkeleton width="w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : plans.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                <p className="text-sm font-medium text-slate-700">No subscription plans yet</p>
                <p className="mt-1 text-[13px] text-slate-400">
                  Add your first plan to start managing pricing and limits.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {plans.map((plan) => {
                  const monthCount = Math.max(1, Math.round(plan.duration_days / 30));
                  const isDeleting = deleteLoadingId === plan.id;
                  const isToggling = toggleLoadingId === plan.id;
                  const showDeleteConfirm = deleteConfirmId === plan.id;

                  return (
                    <div
                      key={plan.id}
                      className={`rounded-xl border bg-white p-5 transition-all hover:border-indigo-200 hover:shadow-sm ${
                        plan.is_popular
                          ? 'border-slate-200 border-l-4 border-l-indigo-500'
                          : 'border-slate-200'
                      } ${plan.is_active ? '' : 'opacity-60'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-950">{plan.title}</h3>
                            {plan.is_popular ? (
                              <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-600">
                                Most Popular
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 text-[13px] text-slate-500">{plan.description}</p>
                        </div>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                          {plan.name}
                        </span>
                      </div>

                      <div className="mt-4">
                        <p className="text-3xl font-bold text-slate-950">₹{plan.price_monthly}/month</p>
                        <p className="mt-1 text-[13px] text-slate-400">
                          ₹{plan.price_monthly * monthCount} for {monthCount} months
                        </p>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                          <p className="text-slate-400">Duration</p>
                          <p className="mt-1 font-medium text-slate-800">{plan.duration_days} days</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                          <p className="text-slate-400">Notifications</p>
                          <p className="mt-1 font-medium text-slate-800">
                            {plan.daily_notification_limit === -1
                              ? 'Unlimited'
                              : `${plan.daily_notification_limit}/day`}
                          </p>
                        </div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                          <p className="text-slate-400">Max shops</p>
                          <p className="mt-1 font-medium text-slate-800">{plan.max_shops}</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                          <p className="text-slate-400">Max QR codes</p>
                          <p className="mt-1 font-medium text-slate-800">{plan.max_qr_codes}</p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          Features
                        </p>
                        <div className="space-y-1.5">
                          {plan.features.map((feature) => (
                            <p key={feature} className="text-[13px] text-slate-700">
                              ✓ {feature}
                            </p>
                          ))}
                        </div>
                      </div>

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <Toggle
                            checked={plan.is_active}
                            onChange={() => void handleTogglePlan(plan.id)}
                            disabled={isToggling}
                          />
                          <span className="text-sm font-medium text-slate-700">
                            {isToggling ? 'Updating...' : plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditDrawer(plan)}
                            className="rounded-lg border border-indigo-200 px-3 py-2 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
                          >
                            Edit
                          </button>

                          {showDeleteConfirm ? (
                            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2">
                              <span className="text-[12px] text-red-700">
                                Are you sure? This cannot be undone.
                              </span>
                              <button
                                type="button"
                                disabled={isDeleting}
                                onClick={() => void handleDeletePlan(plan.id)}
                                className="rounded-md border border-red-200 px-2.5 py-1 text-[12px] font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-60"
                              >
                                {isDeleting ? 'Deleting...' : 'Confirm'}
                              </button>
                              <button
                                type="button"
                                disabled={isDeleting}
                                onClick={() => setDeleteConfirmId(null)}
                                className="rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-600 transition-colors hover:bg-white"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(plan.id)}
                              className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      <div
        className={`fixed inset-0 z-40 transition ${drawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
      >
        <div
          className={`absolute inset-0 bg-slate-950/20 transition-opacity ${
            drawerOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeDrawer}
        />
        <aside
          className={`absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl transition-transform ${
            drawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <form onSubmit={(event) => void handleSubmitPlan(event)} className="flex min-h-full flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  {drawerMode === 'add' ? 'Add Plan' : 'Edit Plan'}
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Configure billing, limits, and feature highlights.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="flex-1 space-y-5 px-6 py-5">
              <Field
                label="Plan ID / Name"
                value={planForm.name}
                onChange={(value) =>
                  updatePlanField('name', value.toLowerCase().replace(/\s+/g, ''))
                }
                error={planErrors.name}
                placeholder="basic"
                disabled={drawerMode === 'edit'}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Title"
                  value={planForm.title}
                  onChange={(value) => updatePlanField('title', value)}
                  error={planErrors.title}
                  placeholder="Starter"
                />
                <Field
                  label="Sort order"
                  value={planForm.sort_order}
                  onChange={(value) => updatePlanField('sort_order', value)}
                  type="number"
                  error={planErrors.sort_order}
                />
              </div>

              <TextareaField
                label="Description"
                value={planForm.description}
                onChange={(value) => updatePlanField('description', value)}
                error={planErrors.description}
                placeholder="A simple plan for growing stores"
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Price per month"
                  value={planForm.price_monthly}
                  onChange={(value) => updatePlanField('price_monthly', value)}
                  type="number"
                  error={planErrors.price_monthly}
                  prefix="₹"
                />
                <Field
                  label="Duration"
                  value={planForm.duration_days}
                  onChange={(value) => updatePlanField('duration_days', value)}
                  type="number"
                  error={planErrors.duration_days}
                  suffix="days"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Daily notification limit"
                  value={planForm.daily_notification_limit}
                  onChange={(value) => updatePlanField('daily_notification_limit', value)}
                  type="number"
                  error={planErrors.daily_notification_limit}
                  helperText="Use -1 for unlimited"
                />
                <Field
                  label="Max shops"
                  value={planForm.max_shops}
                  onChange={(value) => updatePlanField('max_shops', value)}
                  type="number"
                  error={planErrors.max_shops}
                />
              </div>

              <Field
                label="Max QR codes"
                value={planForm.max_qr_codes}
                onChange={(value) => updatePlanField('max_qr_codes', value)}
                type="number"
                error={planErrors.max_qr_codes}
              />

              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label className="block text-xs font-medium text-slate-700">Features</label>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
                  >
                    Add feature
                  </button>
                </div>

                <div className="space-y-2">
                  {planForm.features.map((feature, index) => (
                    <div key={`${index}-${feature}`} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(event) => updateFeature(index, event.target.value)}
                        placeholder="Priority notifications"
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100"
                        aria-label={`Remove feature ${index + 1}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                {planErrors.features ? (
                  <p className="mt-1 text-[11px] text-red-500">{planErrors.features}</p>
                ) : null}
              </div>

              <div className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 sm:grid-cols-2">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Is Popular</p>
                    <p className="text-[12px] text-slate-400">
                      Highlight this as the recommended plan
                    </p>
                  </div>
                  <Toggle
                    checked={planForm.is_popular}
                    onChange={(checked) => updatePlanField('is_popular', checked)}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Is Active</p>
                    <p className="text-[12px] text-slate-400">
                      Control whether shops can use this plan
                    </p>
                  </div>
                  <Toggle
                    checked={planForm.is_active}
                    onChange={(checked) => updatePlanField('is_active', checked)}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
              <button
                type="button"
                onClick={closeDrawer}
                disabled={planSaving}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={planSaving}
                className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600 disabled:opacity-60"
              >
                {planSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </aside>
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
